/**
 * PATCH /api/v1/agent/tickets/{number} mutation.
 *
 * The backend uses pydantic's model_fields_set semantics, so callers
 * must only include keys they actually intend to change. We strip
 * `undefined` keys here as a courtesy so an axios JSON body of
 * `{ status: 'open' }` does not accidentally carry `priority: null`.
 *
 * On success we invalidate the detail, the list, and the counts so the
 * inbox header and rows reflect the new state.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type { TicketResponse, TicketUpdateRequest } from "@/types/api";

import { agentTicketCountsQueryKey } from "./useAgentTicketCounts";
import { agentTicketDetailQueryPrefix } from "./useAgentTicketDetail";
import { ticketEventsQueryKeyPrefix } from "./useTicketEvents";

function stripUndefined(
  body: TicketUpdateRequest,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export function useUpdateTicket(
  ticketNumber: number,
): UseMutationResult<TicketResponse, Error, TicketUpdateRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketUpdateRequest) => {
      const response = await api.patch<TicketResponse>(
        `/agent/tickets/${ticketNumber}`,
        stripUndefined(body),
      );
      return response.data;
    },
    onSuccess: (updated) => {
      // Write the response straight into every cached detail entry
      // (there is at most one active per page) so the field flips
      // immediately, not on the next refetch. The detail cache is
      // keyed by lowercase URL code; this mutation only knows the
      // numeric ticket_number, so we match by prefix instead of
      // computing the key.
      queryClient.setQueriesData<TicketResponse>(
        { queryKey: agentTicketDetailQueryPrefix },
        (prev) => (prev?.ticket_number === ticketNumber ? updated : prev),
      );
      void queryClient.invalidateQueries({
        queryKey: agentTicketDetailQueryPrefix,
      });
      void queryClient.invalidateQueries({
        queryKey: ["agent", "tickets", "list"],
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketCountsQueryKey,
      });
      // The recently-viewed rail renders on the detail page itself and
      // carries the subject, so a rename would otherwise sit stale in
      // the right rail of the very page that just renamed the ticket.
      void queryClient.invalidateQueries({
        queryKey: ["agent", "tickets", "recently-viewed"],
      });
      // Every PATCH emits at least one ticket_event (status_changed,
      // priority_changed, ...). Invalidate the events cache so the
      // audit timeline reflects the change without waiting for the
      // refetch tick.
      void queryClient.invalidateQueries({
        queryKey: ticketEventsQueryKeyPrefix(ticketNumber),
      });
    },
  });
}
